import { useCallback, useEffect, useMemo, useState } from 'react';

export interface RowSelection {
    isSelectMode: boolean;
    toggleSelectMode: () => void;
    exitSelectMode: () => void;
    selectedIds: Set<string>;
    selectedCount: number;
    isSelected: (id: string) => boolean;
    toggleRow: (id: string) => void;
    allSelected: boolean;
    toggleAll: () => void;
}

export function useRowSelection(selectableIds: string[]): RowSelection {
    const [isSelectMode, setIsSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

    const selectableIdsKey = selectableIds.join('|');

    useEffect(() => {
        setSelectedIds((prev) => {
            const selectable = new Set(selectableIds);
            const next = new Set([...prev].filter((id) => selectable.has(id)));
            return next.size === prev.size ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectableIdsKey]);

    const exitSelectMode = useCallback(() => {
        setIsSelectMode(false);
        setSelectedIds(new Set());
    }, []);

    const toggleSelectMode = useCallback(() => {
        setIsSelectMode((prev) => {
            if (prev) {
                setSelectedIds(new Set());
            }
            return !prev;
        });
    }, []);

    const toggleRow = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

    const allSelected = useMemo(
        () => selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id)),
        [selectableIds, selectedIds],
    );

    const toggleAll = useCallback(() => {
        setSelectedIds(allSelected ? new Set() : new Set(selectableIds));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSelected, selectableIdsKey]);

    return {
        isSelectMode,
        toggleSelectMode,
        exitSelectMode,
        selectedIds,
        selectedCount: selectedIds.size,
        isSelected,
        toggleRow,
        allSelected,
        toggleAll,
    };
}
